import { useState } from "react";
import {
  Box,
  Collapse,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TruncateTypography } from "components/TruncateTypography";
import { getAmountCodes } from "encamp-shared/src/constants/storageAmounts";
import { isNjEhsThresholdNaicsCode, NJ_EHS_NAICS_PDF_URL } from "encamp-shared/src/utils/casAndEhs";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { ReportingFacilityChemicalQuery, UnitType } from "generated-graphql/graphql";
import { useProducts } from "hooks/useProducts";
import { Link, useParams } from "react-router-dom";

type ThresholdResult = NonNullable<
  ReportingFacilityChemicalQuery["reportingFacilityChemical"]["thresholdResults"]
>[number];

export function formatThresholdText(
  thresholdResult: ThresholdResult,
  state: string | null | undefined
): string {
  const { description, threshold, unit, overThreshold, amountCompared } = thresholdResult;
  const verb = overThreshold ? "Exceeds" : "Under";

  if (amountCompared == null) {
    const thresholdText = prettyPrintQuantity({
      amount: threshold,
      unit: unit as UnitType,
      state: state,
    });
    return `${verb} ${description} of ${thresholdText}`;
  }

  const difference = Math.abs(amountCompared - threshold);
  const differenceText = prettyPrintQuantity({
    amount: difference,
    unit: unit as UnitType,
    state: state,
  });
  return `${verb} ${description} by ${differenceText}`;
}

function formatEhsForm(adjustment: number): string {
  if (adjustment === 0.2) return "Solution (\u00d70.2)";
  if (adjustment === 0.3) return "Molten (\u00d70.3)";
  return "\u2014";
}

function hasContributions(tr: ThresholdResult): boolean {
  return (tr.allContributions?.length ?? 0) > 0;
}

export const SummaryCard = ({
  rfc,
}: {
  rfc: ReportingFacilityChemicalQuery["reportingFacilityChemical"];
}) => {
  const { hasProducts } = useProducts();
  const maxWidthAmountBoxes = hasProducts ? "20%" : "30%";
  const maxWidthSummaryBoxes = hasProducts ? "27%" : "30%";
  const thresholdResults = rfc.thresholdResults ?? [];

  const isNjFacility = rfc.facility?.state === "NJ";
  const isNaicsExemptFromNjEhs =
    isNjFacility && !isNjEhsThresholdNaicsCode(rfc.facility?.naicsCode);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Box maxWidth={maxWidthAmountBoxes}>
          <Typography variant="body2">Max Daily Amount</Typography>
          <TruncateTypography variant="h6" showTooltip>
            {prettyPrintQuantity({
              amount: rfc.maxAmount,
              unit: rfc.unit,
              state: rfc.facility?.state,
            })}
          </TruncateTypography>
          <TruncateTypography variant="body2" lines={1} showTooltip>
            {rfc.maxAmountCode && rfc.facility?.state
              ? getAmountCodes(rfc.facility.state)[rfc.maxAmountCode]
              : undefined}
          </TruncateTypography>
        </Box>
        <Box flex={1} />
        <Box maxWidth={maxWidthAmountBoxes}>
          <Typography variant="body2">Avg Daily Amount</Typography>
          <TruncateTypography variant="h6" showTooltip>
            {prettyPrintQuantity({
              amount: rfc.averageAmount,
              unit: rfc.unit,
              state: rfc.facility?.state,
            })}
          </TruncateTypography>
          <TruncateTypography variant="body2" showTooltip>
            {rfc.averageAmountCode && rfc.facility?.state
              ? getAmountCodes(rfc.facility.state)[rfc.averageAmountCode]
              : undefined}
          </TruncateTypography>
        </Box>

        <Box flex={1} />
        {hasProducts && (
          <>
            <Box display="flex" flexDirection="column" gap={1} maxWidth={maxWidthSummaryBoxes}>
              <LineItem
                label="Products"
                value={
                  rfc.maxAmountProductChemicalBreakdown?.productMaxAmount
                    ? prettyPrintQuantity({
                        amount: rfc.maxAmountProductChemicalBreakdown.productMaxAmount.amount,
                        unit: rfc.maxAmountProductChemicalBreakdown.productMaxAmount.unit,
                        state: rfc.facility?.state,
                      })
                    : prettyPrintQuantity({
                        amount: 0,
                        unit: rfc.unit,
                        state: rfc.facility?.state,
                      })
                }
                valueColumnAlign="left"
              />
              <LineItem
                label="Other Chemicals"
                value={
                  rfc.maxAmountProductChemicalBreakdown?.chemicalMaxAmount
                    ? prettyPrintQuantity({
                        amount: rfc.maxAmountProductChemicalBreakdown.chemicalMaxAmount.amount,
                        unit: rfc.maxAmountProductChemicalBreakdown.chemicalMaxAmount.unit,
                        state: rfc.facility?.state,
                      })
                    : prettyPrintQuantity({
                        amount: 0,
                        unit: rfc.unit,
                        state: rfc.facility?.state,
                      })
                }
                valueColumnAlign="left"
              />
              <LineItem
                label="Total"
                value={prettyPrintQuantity({
                  amount: rfc.maxAmount,
                  unit: rfc.unit,
                  state: rfc.facility?.state,
                })}
                bold
                valueColumnAlign="left"
              />
            </Box>
            <Box flex={1} />
          </>
        )}
        <Box display="flex" flexDirection="column" gap={1} maxWidth={maxWidthSummaryBoxes}>
          <LineItem
            label="Largest Container"
            value={prettyPrintQuantity({
              amount: rfc.maxAmountLargestContainer,
              unit: rfc.unit,
              state: rfc.facility?.state,
            })}
            valueColumnAlign="right"
          />
          <LineItem
            label="Days on Site"
            value={`${rfc.daysOnSite} days`}
            valueColumnAlign="right"
          />
          <LineItem
            label={`Last Year (${rfc.reportingYear - 1})`}
            value={
              rfc.previousYearMaxAmount
                ? prettyPrintQuantity({
                    amount: rfc.previousYearMaxAmount.amount,
                    unit: rfc.previousYearMaxAmount.unit,
                    state: rfc.facility?.state,
                  })
                : "N/A"
            }
            valueColumnAlign="right"
          />
        </Box>
      </Box>
      {thresholdResults.length > 0 && !rfc.aggregationErrorMessage && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Reporting Thresholds
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {thresholdResults.map((tr, index) => (
              <ThresholdLineItem
                key={index}
                thresholdResult={tr}
                state={rfc.facility?.state}
              />
            ))}
          </Box>
        </Box>
      )}
      {isNaicsExemptFromNjEhs && (
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip
            title={
              <span>
                This facility's NAICS code ({rfc.facility?.naicsCode}) exempts it from NJ EHS
                thresholds. NJ CRTK EHS thresholds only apply to facilities with specific NAICS
                codes. View the{" "}
                <a
                  href={NJ_EHS_NAICS_PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit" }}
                >
                  applicable NAICS list
                </a>
                .
              </span>
            }
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <InfoOutlinedIcon fontSize="small" color="info" />
              <Typography variant="body2" color="text.secondary">
                This facility's NAICS code ({rfc.facility?.naicsCode}) exempts it from NJ EHS
                thresholds.
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
};

const ThresholdLineItem = ({
  thresholdResult,
  state,
}: {
  thresholdResult: ThresholdResult;
  state: string | null | undefined;
}) => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [expanded, setExpanded] = useState(false);
  const expandable = hasContributions(thresholdResult);
  const contributions = thresholdResult.allContributions ?? [];

  const total = contributions.reduce(
    (sum, c) => sum + c.contributingAmount,
    0
  );

  return (
    <Box component="li" sx={{ mb: expandable ? 1 : 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: expandable ? "pointer" : "default",
        }}
        onClick={expandable ? () => setExpanded((prev) => !prev) : undefined}
      >
        {expandable && (
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              mr: 0.5,
              transition: "transform 0.2s",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        )}
        <Typography variant="body2">
          {formatThresholdText(thresholdResult, state)}
        </Typography>
      </Box>
      {expandable && (
        <Collapse in={expanded}>
          <Table size="small" sx={{ mt: 1, mb: 1, ml: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>
                  Chemical
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", py: 0.5 }}
                >
                  CAS %
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", py: 0.5 }}
                >
                  EHS Form
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", py: 0.5 }}
                >
                  Contribution
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contributions.map((c) => (
                <TableRow key={c.chemicalId}>
                  <TableCell sx={{ py: 0.5 }}>
                    <MuiLink
                      component={Link}
                      to={`/o/${tenantId}/chemicals/catalog/chemicals/${c.chemicalId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {c.chemicalName ?? c.chemicalId}
                    </MuiLink>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {c.componentPercentage}%
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {formatEhsForm(c.ehsFormAdjustment)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {prettyPrintQuantity({
                      amount: c.contributingAmount,
                      unit: thresholdResult.unit as UnitType,
                      state,
                    })}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{ fontWeight: "bold", py: 0.5, borderBottom: "none" }}
                >
                  Total
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", py: 0.5, borderBottom: "none" }}
                >
                  {prettyPrintQuantity({
                    amount: total,
                    unit: thresholdResult.unit as UnitType,
                    state,
                  })}{" "}
                  vs{" "}
                  {prettyPrintQuantity({
                    amount: thresholdResult.threshold,
                    unit: thresholdResult.unit as UnitType,
                    state,
                  })}{" "}
                  threshold
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Collapse>
      )}
    </Box>
  );
};

const LineItem: React.FC<{
  label: string;
  value: string | number | undefined;
  bold?: boolean;
  valueColumnAlign?: "left" | "right";
}> = ({ label, value, bold }) => {
  const fontWeight = bold ? "bold" : undefined;
  return (
    <Box display="flex" alignItems="baseline" flexWrap="nowrap">
      <Typography fontWeight={fontWeight} variant="body2" noWrap flexShrink={0}>
        {label}
      </Typography>
      <Box
        sx={{
          minWidth: "1rem",
          flex: 1,
          borderBottom: value ? "1px dotted gray" : undefined,
          mx: 0.5,
        }}
      />
      <TruncateTypography fontWeight={fontWeight} variant="body2" showTooltip>
        {value}
      </TruncateTypography>
    </Box>
  );
};
